import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { from, map, Observable, of } from 'rxjs';
import { AuthService } from '../../services/core/auth.service';

@Injectable({
  providedIn: 'root',
})

export class AuthGuard implements CanActivate {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly router: Router = inject(Router);

  constructor() { }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Do not block the /login route
    if (state.url === '/auth') {
      return of(true);
    }

    // If token exists, allow access
    if (this.authService.isLoggedIn()) {
      return of(true);
    }

    if (this.authService.isAuthenticated()) {
      return of(true);
    }

    // The SSO access token looks expired (or was simply never loaded into memory yet —
    // e.g. right after a full page reload). Attempt one silent refresh via the httpOnly
    // refresh cookie before giving up: the underlying session is often still valid for up
    // to 14 days, well past the 15-minute access-token TTL that isAuthenticated() alone
    // would otherwise enforce on every navigation.
    return from(this.authService.refresh()).pipe(
      map((ok) => {
        if (ok) {
          return true;
        }

        this.authService.requireLogin(state.url);
        return false;
      }),
    );
  }
}
